import React, { useState, useEffect } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import toast from 'react-hot-toast';
import { publicApi } from '../utils/api';

const CALENDLY_URL = 'https://calendly.com/privatebrands-contact/30min';

/**
 * Modal pentru solicitare demo. Apelat din landing page (header CTA + pricing teaser).
 *
 * Are 2 mod-uri:
 *  - 'calendar' (default) — CTA care deschide Calendly intr-un tab nou
 *    (inline iframe a fost scos: Chrome 120+ third-party-cookies-off + extensiile
 *    de privacy blocau iframe-ul, modalul aparea gol)
 *  - 'form' — formular contact pentru cei care vor follow-up email
 *
 * Props:
 *   - open: boolean
 *   - onClose: () => void
 *   - source: string — tracking
 */
export default function DemoRequestModal({ open, onClose, source = 'unknown' }) {
  // keyPrefix — toate cheile componentei stau sub `app.modals.demo` in
  // translation.json. Fara prefix, t('header.eyebrow') nu rezolva nimic si
  // i18next afiseaza numele cheii brute in UI.
  const { t } = useTranslation('translation', { keyPrefix: 'app.modals.demo' });
  const [mode, setMode] = useState('calendar');
  const [form, setForm] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    role: '',
    industry: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
      setSuccess(false);
      setMode('calendar');
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  if (!open) return null;

  const handleChange = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await publicApi.demoRequest({ ...form, source });
      if (res.data?.success) {
        setSuccess(true);
        toast.success(t('toast.success'));
      } else {
        toast.error(res.data?.message || t('toast.error_retry'));
      }
    } catch (err) {
      toast.error(t('toast.error_contact'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(13,27,42,0.7)',
        backdropFilter: 'blur(4px)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#FFFFFF',
          borderRadius: 20,
          maxWidth: 560,
          width: '100%',
          maxHeight: '92vh',
          overflowY: 'auto',
          boxShadow: '0 30px 80px rgba(13,27,42,0.4)',
          position: 'relative',
          transition: 'max-width 0.3s ease',
        }}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          aria-label={t('close_aria')}
          style={{
            position: 'absolute',
            top: 16,
            right: 16,
            background: '#F3F4F6',
            border: 'none',
            width: 36,
            height: 36,
            borderRadius: '50%',
            fontSize: 18,
            cursor: 'pointer',
            color: '#6B7280',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1,
          }}
        >
          ×
        </button>

        {success ? (
          <div style={{ padding: '64px 32px', textAlign: 'center' }}>
            <div style={{ fontSize: 64, marginBottom: 16 }}>✅</div>
            <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: 28, fontWeight: 700, color: '#0A1428', marginBottom: 12 }}>
              {t('success.title')}
            </h2>
            <p style={{ fontSize: 16, color: '#4B5563', lineHeight: 1.6, fontFamily: 'Inter, system-ui, sans-serif', marginBottom: 24 }}>
              <Trans i18nKey="app.modals.demo.success.body" values={{ email: form.email }} components={{ 1: <strong />, 3: <strong /> }} />
            </p>
            <p style={{ fontSize: 14, color: '#6B7280', marginBottom: 20, fontFamily: 'Inter, system-ui, sans-serif' }}>
              {t('success.accelerate')}
            </p>
            <a
              href="https://calendly.com/privatebrands-contact/30min"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '14px 28px',
                background: 'linear-gradient(135deg, #D4AF37 0%, #B8972E 100%)',
                color: '#0A1428',
                textDecoration: 'none',
                borderRadius: 10,
                fontFamily: 'Inter, system-ui, sans-serif',
                fontSize: 14,
                fontWeight: 700,
                marginBottom: 16,
                boxShadow: '0 6px 16px rgba(212,175,55,0.3)',
              }}
            >
              {t('success.schedule_now')}
            </a>
            <div>
              <button
                onClick={onClose}
                style={{
                  padding: '12px 24px',
                  background: 'transparent',
                  color: '#6B7280',
                  border: 'none',
                  fontFamily: 'Inter, system-ui, sans-serif',
                  fontSize: 13,
                  cursor: 'pointer',
                  textDecoration: 'underline',
                }}
              >
                {t('success.or_close')}
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Header */}
            <div style={{ padding: '32px 32px 0' }}>
              <p style={{ fontSize: 11, fontWeight: 600, color: '#D4AF37', textTransform: 'uppercase', letterSpacing: 3, marginBottom: 8, fontFamily: 'DM Mono, monospace' }}>
                {t('header.eyebrow')}
              </p>
              <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(22px, 3vw, 28px)', fontWeight: 700, color: '#0A1428', marginBottom: 8, lineHeight: 1.2 }}>
                {mode === 'calendar' ? t('header.title_calendar') : t('header.title_form')}
              </h2>
              <p style={{ fontSize: 14, color: '#6B7280', lineHeight: 1.6, fontFamily: 'Inter, system-ui, sans-serif', marginBottom: 20 }}>
                {mode === 'calendar'
                  ? t('header.subtitle_calendar')
                  : t('header.subtitle_form')}
              </p>

              {/* Tabs toggle Calendar / Form */}
              <div style={{ display: 'flex', gap: 4, padding: 4, background: '#F3F4F6', borderRadius: 10, marginBottom: 0 }}>
                <button
                  type="button"
                  onClick={() => setMode('calendar')}
                  style={{
                    flex: 1,
                    padding: '10px 12px',
                    background: mode === 'calendar' ? '#FFFFFF' : 'transparent',
                    border: 'none',
                    borderRadius: 8,
                    cursor: 'pointer',
                    fontFamily: 'Inter, system-ui, sans-serif',
                    fontSize: 13,
                    fontWeight: 600,
                    color: mode === 'calendar' ? '#0A1428' : '#6B7280',
                    boxShadow: mode === 'calendar' ? '0 2px 8px rgba(13,27,42,0.08)' : 'none',
                    transition: 'all 0.2s',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 6,
                  }}
                >
                  {t('tabs.calendar')}
                </button>
                <button
                  type="button"
                  onClick={() => setMode('form')}
                  style={{
                    flex: 1,
                    padding: '10px 12px',
                    background: mode === 'form' ? '#FFFFFF' : 'transparent',
                    border: 'none',
                    borderRadius: 8,
                    cursor: 'pointer',
                    fontFamily: 'Inter, system-ui, sans-serif',
                    fontSize: 13,
                    fontWeight: 600,
                    color: mode === 'form' ? '#0A1428' : '#6B7280',
                    boxShadow: mode === 'form' ? '0 2px 8px rgba(13,27,42,0.08)' : 'none',
                    transition: 'all 0.2s',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 6,
                  }}
                >
                  {t('tabs.form')}
                </button>
              </div>
            </div>

            {/* Tab 'calendar' — CTA direct catre Calendly in tab nou.
                Inline iframe a fost scos pentru ca era blocat de Chrome 120+
                (third-party cookies disabled) + extensii de privacy/ad-block,
                lasand modalul gol. Pattern-ul recomandat oficial Calendly
                pentru cazuri ca acestea este popup/tab nou. */}
            {mode === 'calendar' && (
              <div style={{ padding: '24px 32px 32px', textAlign: 'center' }}>
                <div style={{
                  background: 'linear-gradient(135deg, #FAF8F2 0%, #F5F0E1 100%)',
                  border: '1px solid #E8DCC0',
                  borderRadius: 14,
                  padding: '28px 24px',
                  marginBottom: 16,
                }}>
                  <div style={{ fontSize: 44, marginBottom: 12, lineHeight: 1 }}>📅</div>
                  <h3 style={{
                    fontFamily: 'Playfair Display, serif',
                    fontSize: 20, fontWeight: 700, color: '#0A1428',
                    marginBottom: 8, lineHeight: 1.3,
                  }}>
                    {t('calendar.host_line')}
                  </h3>
                  <p style={{
                    fontSize: 14, color: '#4B5563', lineHeight: 1.6,
                    fontFamily: 'Inter, system-ui, sans-serif', marginBottom: 22,
                    maxWidth: 420, marginLeft: 'auto', marginRight: 'auto',
                  }}>
                    {t('calendar.description')}
                  </p>
                  <a
                    href={CALENDLY_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '16px 32px',
                      background: 'linear-gradient(135deg, #D4AF37 0%, #B8972E 100%)',
                      color: '#0A1428',
                      textDecoration: 'none',
                      borderRadius: 10,
                      fontFamily: 'DM Mono, monospace',
                      fontSize: 13,
                      fontWeight: 700,
                      letterSpacing: 1.5,
                      textTransform: 'uppercase',
                      boxShadow: '0 8px 20px rgba(212,175,55,0.35)',
                    }}
                  >
                    {t('calendar.open_button')}
                  </a>
                </div>
                <p style={{
                  fontSize: 13, color: '#6B7280',
                  fontFamily: 'Inter, system-ui, sans-serif',
                  margin: 0,
                }}>
                  {t('calendar.prefer_contact')}{' '}
                  <button
                    type="button"
                    onClick={() => setMode('form')}
                    style={{
                      background: 'none', border: 'none', padding: 0,
                      color: '#B8972E', fontWeight: 600, cursor: 'pointer',
                      fontFamily: 'inherit', fontSize: 'inherit',
                      textDecoration: 'underline',
                    }}
                  >
                    {t('calendar.leave_details')}
                  </button>
                </p>
              </div>
            )}

            <form onSubmit={handleSubmit} style={{ padding: '20px 32px 32px', display: mode === 'form' ? 'block' : 'none' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                <Field label={t('form.name_label')} required value={form.name} onChange={handleChange('name')} placeholder={t('form.name_placeholder')} autoComplete="name" />
                <Field label={t('form.email_label')} required type="email" value={form.email} onChange={handleChange('email')} placeholder={t('form.email_placeholder')} autoComplete="email" />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                <Field label={t('form.company_label')} required value={form.company} onChange={handleChange('company')} placeholder={t('form.company_placeholder')} autoComplete="organization" />
                <Field label={t('form.phone_label')} type="tel" value={form.phone} onChange={handleChange('phone')} placeholder={t('form.phone_placeholder')} autoComplete="tel" />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                <Field label={t('form.role_label')} value={form.role} onChange={handleChange('role')} placeholder={t('form.role_placeholder')} autoComplete="organization-title" />
                <Field
                  label={t('form.industry_label')}
                  value={form.industry}
                  onChange={handleChange('industry')}
                  placeholder="Banking"
                  type="select"
                  options={[
                    { value: '', label: t('form.industry.select') },
                    { value: 'banking', label: t('form.industry.banking') },
                    { value: 'retail', label: t('form.industry.retail') },
                    { value: 'sanatate', label: t('form.industry.health') },
                    { value: 'imobiliare', label: t('form.industry.real_estate') },
                    { value: 'telecom', label: t('form.industry.telecom') },
                    { value: 'turism', label: t('form.industry.tourism') },
                    { value: 'auto', label: t('form.industry.auto') },
                    { value: 'avocatura', label: t('form.industry.legal') },
                    { value: 'altele', label: t('form.industry.other') },
                  ]}
                />
              </div>
              <div style={{ marginBottom: 16 }}>
                <Field
                  label={t('form.message_label')}
                  value={form.message}
                  onChange={handleChange('message')}
                  placeholder={t('form.message_placeholder')}
                  type="textarea"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                style={{
                  width: '100%',
                  padding: '16px 24px',
                  background: submitting ? '#B8972E' : '#D4AF37',
                  color: '#0A1428',
                  border: 'none',
                  borderRadius: 10,
                  fontFamily: 'DM Mono, monospace',
                  fontSize: 13,
                  letterSpacing: 1.5,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                }}
              >
                {submitting ? t('form.submitting') : t('form.submit')}
              </button>

              <p style={{ marginTop: 16, fontSize: 11, color: '#9CA3AF', textAlign: 'center', fontFamily: 'DM Mono, monospace', letterSpacing: 0.5 }}>
                {t('form.privacy_note')}
              </p>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

/* Helper field component */
function Field({ label, value, onChange, placeholder, type = 'text', required, options, autoComplete }) {
  const inputStyle = {
    width: '100%',
    padding: '12px 14px',
    border: '1px solid #E5E7EB',
    borderRadius: 8,
    fontSize: 14,
    fontFamily: 'Inter, system-ui, sans-serif',
    color: '#0A1428',
    background: '#FFFFFF',
    boxSizing: 'border-box',
    outline: 'none',
    transition: 'border-color 0.2s, box-shadow 0.2s',
  };
  return (
    <label style={{ display: 'block' }}>
      <span style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#0A1428', marginBottom: 6, fontFamily: 'DM Mono, monospace', letterSpacing: 0.5, textTransform: 'uppercase' }}>
        {label}
      </span>
      {type === 'textarea' ? (
        <textarea
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          required={required}
          rows={3}
          style={{ ...inputStyle, resize: 'vertical', minHeight: 80 }}
          onFocus={(e) => { e.target.style.borderColor = '#D4AF37'; e.target.style.boxShadow = '0 0 0 3px rgba(212,175,55,0.1)'; }}
          onBlur={(e) => { e.target.style.borderColor = '#E5E7EB'; e.target.style.boxShadow = 'none'; }}
        />
      ) : type === 'select' ? (
        <select
          value={value}
          onChange={onChange}
          required={required}
          style={inputStyle}
          onFocus={(e) => { e.target.style.borderColor = '#D4AF37'; e.target.style.boxShadow = '0 0 0 3px rgba(212,175,55,0.1)'; }}
          onBlur={(e) => { e.target.style.borderColor = '#E5E7EB'; e.target.style.boxShadow = 'none'; }}
        >
          {options.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
        </select>
      ) : (
        <input
          type={type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          required={required}
          autoComplete={autoComplete}
          style={inputStyle}
          onFocus={(e) => { e.target.style.borderColor = '#D4AF37'; e.target.style.boxShadow = '0 0 0 3px rgba(212,175,55,0.1)'; }}
          onBlur={(e) => { e.target.style.borderColor = '#E5E7EB'; e.target.style.boxShadow = 'none'; }}
        />
      )}
    </label>
  );
}
